import { IntegrationLogLevel, IntegrationScriptLogDto } from '@/lib/api/types.gen';
import { Accordion, AccordionContent, AccordionTrigger } from '@/components/ui/accordion';
import { AccordionItem } from '@radix-ui/react-accordion';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatDate } from 'date-fns';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';

const LogVariants = {
  [IntegrationLogLevel.Debug]: 'outline',
  [IntegrationLogLevel.Info]: 'default',
  [IntegrationLogLevel.Warning]: 'warning',
  [IntegrationLogLevel.Error]: 'destructive',
} as const;

export const IntegrationLogs = ({ logs }: { logs: IntegrationScriptLogDto[] }) => {
  return (
    <div className="m-4  ">
      <Accordion type="multiple" defaultValue={['logs']} className='rounded-lg border bg-[#111]/40'>
        <AccordionItem value="logs">
          <AccordionTrigger className="  text-lime font-semibold p-4 hover:no-underline ">
            <div className="flex items-center gap-x-2">
              Logs ({logs.length}){' '}
              {logs.some((x) => x.level === IntegrationLogLevel.Error) ? (
                <Badge variant="destructive">Error</Badge>
              ) : null}
            </div>
          </AccordionTrigger>
          <AccordionContent className="relative flex flex-1 flex-col gap-2 overflow-x-scroll border-t">
            <Table>
              <TableBody>
                {logs.map((log, index) => {
                  let message = log.message;
                  let isJson = false;

                  if (message?.startsWith('{') && message?.endsWith('}')) {
                    try {
                      message = JSON.stringify(JSON.parse(message), null, 2);
                      isJson = true;
                    } catch (error) {
                      console.error(error);
                    }
                  }

                  return (
                    <TableRow key={index} className="max-w-full overflow-scroll">
                      <TableCell>
                        <p className="font-mono text-xs font-medium opacity-50 whitespace-nowrap">
                          {log.timestamp ? formatDate(new Date(log.timestamp), 'h:mm:ss a') : ''}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={LogVariants[log.level ?? IntegrationLogLevel.Debug]}
                          className="text-white"
                        >
                          {log.level}
                        </Badge>
                      </TableCell>
                      <TableCell className="w-full">
                        <div className="flex flex-row items-center gap-3">
                          <div className="flex-1">
                            {isJson ? (
                              <Collapsible className="w-full">
                                <div className="flex w-full items-center justify-between">
                                  <Badge variant="outline" className='text-white bg-muted/10'>JSON Output</Badge>
                                  <CollapsibleTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="rounded-none text-xs"
                                    >
                                      <ChevronDown />
                                    </Button>
                                  </CollapsibleTrigger>
                                </div>
                                <CollapsibleContent className="font-mono whitespace-pre-wrap bg-green/40 p-3 rounded-md mt-2">
                                  {message}
                                </CollapsibleContent>
                              </Collapsible>
                            ) : (
                              <p className="flex-1 whitespace-pre-wrap">{message}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
